import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

// The callers parse the value Bronson's flatpickr-based picker produced and
// were not changed: ISO dates (`Y-m-d`, which is exactly what a native date
// input yields) joined by its German-locale range separator. They ignore any
// value without the separator, so a half-chosen range changes nothing.
export const RANGE_SEPARATOR = ' bis ';

interface DatePickerProps {
  mode?: 'single' | 'range';
  value?: string;
  onChange?: (value: string) => void;
  testId?: string;
  className?: string;
}

function splitRange(value?: string): [string, string] {
  const [start = '', stop = ''] = (value ?? '').split(RANGE_SEPARATOR);
  return [start, stop];
}

export function DatePicker({ mode = 'single', value, onChange, testId, className }: DatePickerProps) {
  const [from, setFrom] = useState(() => splitRange(value)[0]);
  const [to, setTo] = useState(() => splitRange(value)[1]);

  // Follow the parent when it sets or clears the value, e.g. "Reset filters".
  useEffect(() => {
    const [start, stop] = splitRange(value);
    setFrom(start);
    setTo(stop);
  }, [value]);

  if (mode === 'single') {
    return (
      <div className={classNames('ec-datepicker', className)}>
        <input
          type="date"
          className="ec-input__field"
          data-testid={testId}
          value={from}
          aria-label="Date"
          onChange={e => {
            setFrom(e.target.value);
            onChange?.(e.target.value);
          }}
        />
      </div>
    );
  }

  const emit = (start: string, stop: string) => {
    if (start && stop) {
      const [first, last] = start <= stop ? [start, stop] : [stop, start];
      onChange?.(`${first}${RANGE_SEPARATOR}${last}`);
    } else {
      onChange?.(start || stop);
    }
  };

  return (
    <div className={classNames('ec-datepicker ec-datepicker--range', className)} data-testid={testId}>
      <input
        type="date"
        className="ec-input__field"
        aria-label="From"
        value={from}
        max={to || undefined}
        onChange={e => {
          setFrom(e.target.value);
          emit(e.target.value, to);
        }}
      />
      <span className="ec-datepicker__separator" aria-hidden="true">–</span>
      <input
        type="date"
        className="ec-input__field"
        aria-label="To"
        value={to}
        min={from || undefined}
        onChange={e => {
          setTo(e.target.value);
          emit(from, e.target.value);
        }}
      />
    </div>
  );
}
